import { useState, useCallback, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { downloadFile, extractFileNameFromHeaders } from '../../../../util/DownloadUtil';

import {
  retrieveSiteQueryStatusPerMonthReport,
  downloadSiteQueryStatusPerMonthReport,
} from '../../../../services/ReportService';

import { Table } from '../../../common/table/Table';
import { SaveButton } from '../../../common/button/save-button/SaveButton';

import './SiteQueryStatusPerMonth.css';

export const SiteQueryStatusPerMonth = ({
  facility,
  dates,
  resetFilters,
  context,
  setSpinnerText,
  showToaster,
  downloadEnabled,
}) => {
  const [heading, setHeading] = useState('');
  const [results, setResults] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const generateReport = useCallback(async () => {
    if (!facility || !dates) return;

    setSpinnerText('Generating Report');
    const [data, error] = await retrieveSiteQueryStatusPerMonthReport(context.username, {
      facilityExternalReference: facility.externalReference,
      dates,
    });
    if (data) {
      if (data.responseStatus === 'Success') {
        setHeading(data.heading);
        setResults(data.siteQueryStatusPerMonthVOS);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  }, [context, setSpinnerText, showToaster, facility, dates]);

  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const handleDownloadExcelClick = async () => {
    setSpinnerText('Downloading Site Query Status per Month Report');
    const [data, headers, error] = await downloadSiteQueryStatusPerMonthReport(context.username, {
      facilityExternalReference: facility.externalReference,
      dates,
    });
    if (data) {
      const name = extractFileNameFromHeaders(headers);
      downloadFile(data, name);
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(undefined);
  };

  const mapDataForGraph = () => {
    const data = [];
    results.forEach((result) => {
      const { month, answered, closed, invalidQuery, open, reIssued } = result;

      if (month !== 'Grand Total') {
        data.push({
          name: month,
          answered: answered === '--' ? 0 : answered,
          closed: closed === '--' ? 0 : closed,
          invalid: invalidQuery === '--' ? 0 : invalidQuery,
          open: open === '--' ? 0 : open,
          reissued: reIssued === '--' ? 0 : reIssued,
        });
      }
    });

    return data;
  };

  const renderLegend = () => {
    const firstResult = results[0];

    return (
      <section className="site-query-status-per-month-legend">
        <p className="site-query-status-per-month-legend-heading">Legend</p>
        <p className="site-query-status-per-month-legend-heading">Grand Totals</p>

        <div className="site-query-status-per-month-legend-item">
          <span className="answered" />
          <p>Answered</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.answered || 0}</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <span className="closed" />
          <p>Closed</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.closed || 0}</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <span className="invalid" />
          <p>Invalid</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.invalidQuery || 0}</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <span className="open" />
          <p>Open</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.open || 0}</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <span className="reissued" />
          <p>Re-Issued</p>
        </div>

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.reIssued || 0}</p>
        </div>

        <div className="site-query-status-per-month-legend-item" />

        <div className="site-query-status-per-month-legend-item">
          <p>{firstResult?.grandTotal || 0}</p>
        </div>
      </section>
    );
  };

  return (
    <section className="site-query-status-per-month-container">
      <div className="site-query-status-per-month-content">
        <h1 className="site-query-status-per-month-heading">{heading}</h1>
        {showTable ? (
          <Table
            tableData={results}
            tableTitle="Query Status per Month"
            maxRowCount={9}
            hiddenColumns={[]}
            searchEnabled
            filterEnabled
            stickyFirstRow
            horizontalScroll
            skipInitialSort
          />
        ) : (
          <section className="site-query-status-per-month-graph">
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={mapDataForGraph()}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line dataKey="answered" stackId="a" stroke="#DC602E" dot={false} strokeWidth="2px" />
                <Line dataKey="closed" stackId="a" stroke="#74A57F" dot={false} strokeWidth="2px" />
                <Line dataKey="invalid" stackId="a" stroke="#6F8695" dot={false} strokeWidth="2px" />
                <Line dataKey="open" stackId="a" stroke="#CD2026" dot={false} strokeWidth="2px" />
                <Line dataKey="reissued" stackId="a" stroke="#E0AC26" dot={false} strokeWidth="2px" />
              </LineChart>
            </ResponsiveContainer>

            {renderLegend()}
          </section>
        )}
      </div>
      <div className="site-query-status-per-month-buttons">
        <SaveButton
          title={`View as ${showTable ? 'Graph' : 'Table'}`}
          onClick={() => setShowTable(!showTable)}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        <SaveButton
          title="Reset Filters"
          onClick={resetFilters}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        {downloadEnabled && (
          <SaveButton
            title="Download Excel"
            onClick={handleDownloadExcelClick}
            color="#077187"
            border="1px solid #077187"
          />
        )}
      </div>
    </section>
  );
};
